import { useCallback, useEffect, useRef, useState } from 'react';
import { updateItemFields, type ItemPatch } from '../db/items';
import { normaliseUrl } from '../import/run';

/**
 * Inline editing, autosaved. No edit mode, no save button, no dialog.
 *
 * Every field is always live, so the record is the source of truth and a
 * draft only exists between a keystroke and the write that follows it. A
 * draft outranks the record until the item changes: a live query landing
 * mid-sentence must not yank the cursor back.
 *
 * Autosave means nobody pressed anything, so a failed write has no natural
 * place to complain. It sets `error`, which the panel keeps on screen until
 * the next write succeeds — a toast would take the news away with it.
 */

export type Field = 'title' | 'price' | 'description' | 'notes' | 'sourceUrl';

/** Long enough to cover a sentence, short enough to survive a crash. */
const DEBOUNCE_MS = 600;
const SAVED_MS = 1600;

const FAILED = "That change couldn't be saved. Copy anything you'd hate to lose before closing.";

type Drafts = Partial<Record<Field, string>>;

export type Edits = {
  /** The draft if there is one, otherwise what's in the database. */
  value: (field: Field, stored: string) => string;
  /** A keystroke. Writes `DEBOUNCE_MS` after the last one. */
  set: (field: Field, next: string) => void;
  /** Blur or Enter. Writes now if anything is still pending. */
  commit: (field: Field) => void;
  /** Drives the quiet indicator near the panel header. */
  saved: boolean;
  error: string | undefined;
};

export function useItemEdits(itemId: string): Edits {
  const [drafts, setDrafts] = useState<Drafts>({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  // Refs, not state: a debounce that re-rendered on every keystroke would
  // reschedule itself and never fire.
  const pending = useRef<Drafts>({});
  const timers = useRef(new Map<Field, number>());
  const savedTimer = useRef<number | undefined>(undefined);

  const write = useCallback(async (id: string, field: Field, raw: string) => {
    try {
      await updateItemFields(id, patchFor(field, raw));
      setError(undefined);
      setSaved(true);
      window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSaved(false), SAVED_MS);
    } catch {
      setError(FAILED);
    }
  }, []);

  const flush = useCallback(
    (id: string, field: Field) => {
      const timer = timers.current.get(field);
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timers.current.delete(field);
      }

      const raw = pending.current[field];
      if (raw === undefined) return;
      delete pending.current[field];
      void write(id, field, raw);
    },
    [write],
  );

  // Cleanup runs with the id these edits belong to, which is what makes
  // clicking straight from one item to the next safe: the half-typed note
  // lands on the item it was typed into, not on the one now on screen.
  useEffect(() => {
    return () => {
      for (const field of Object.keys(pending.current) as Field[]) flush(itemId, field);
      setDrafts({});
    };
  }, [itemId, flush]);

  useEffect(() => {
    const running = timers.current;
    return () => {
      for (const timer of running.values()) window.clearTimeout(timer);
      window.clearTimeout(savedTimer.current);
    };
  }, []);

  const set = useCallback(
    (field: Field, next: string) => {
      setDrafts((was) => ({ ...was, [field]: next }));
      pending.current[field] = next;

      window.clearTimeout(timers.current.get(field));
      timers.current.set(
        field,
        window.setTimeout(() => flush(itemId, field), DEBOUNCE_MS),
      );
    },
    [itemId, flush],
  );

  const commit = useCallback(
    (field: Field) => {
      // Normalising the draft as well as the patch is what stops the field
      // sitting on "  Stool  " or "1,299.50" after the trimmed, parsed
      // version has been written. It runs whether or not a write is still
      // pending: the debounce may already have saved this text.
      const raw = pending.current[field];
      if (raw !== undefined) pending.current[field] = normalise(field, raw);

      setDrafts((was) => {
        const current = raw ?? was[field];
        if (current === undefined) return was;
        return { ...was, [field]: normalise(field, current) };
      });

      flush(itemId, field);
    },
    [itemId, flush],
  );

  const value = useCallback(
    (field: Field, stored: string) => drafts[field] ?? stored,
    [drafts],
  );

  return { value, set, commit, saved, error };
}

/** Digits, one separator, an optional minus. Anything else can't be a price. */
export function priceInput(raw: string): string {
  return raw.replace(/[^\d.,-]/g, '');
}

/** The record's price as the field shows it. Empty when there isn't one. */
export function priceText(price: number | undefined): string {
  return price === undefined ? '' : String(price);
}

function normalise(field: Field, raw: string): string {
  switch (field) {
    case 'title': {
      const trimmed = raw.trim();
      // Matches what item creation does with a blank title: an item with no
      // name at all reads as a bug on the board.
      return trimmed === '' ? 'Untitled' : trimmed;
    }
    case 'price':
      return priceText(parsePrice(raw));
    case 'sourceUrl': {
      // "westelm.com/andes" is a link to a person. Normalising on blur means
      // the field ends up holding something openable, but text that isn't a
      // link at all is left exactly as typed rather than mangled.
      const trimmed = raw.trim();
      return trimmed === '' ? '' : (normaliseUrl(trimmed) ?? trimmed);
    }
    default:
      return raw;
  }
}

function patchFor(field: Field, raw: string): ItemPatch {
  switch (field) {
    case 'title':
      return { title: normalise('title', raw) };
    case 'price':
      return { price: parsePrice(raw) };
    case 'description':
      return { description: raw };
    case 'notes':
      return { notes: raw };
    case 'sourceUrl': {
      const normalised = normalise('sourceUrl', raw);
      // Dexie deletes a key whose value is undefined, which is what clearing
      // the field should do — an item with no link, not an item with "".
      return { sourceUrl: normalised === '' ? undefined : normalised };
    }
  }
}

function parsePrice(raw: string): number | undefined {
  const cleaned = raw.replace(/,/g, '').trim();
  if (cleaned === '') return undefined;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : undefined;
}
